import { useEffect, useMemo, useRef, useState } from 'react';
import { fetchCpvs } from '../../../api/fetchCpvs';
import { VEHICLE_MODEL_TYPE_ORDER, type VehicleModelType, getVehicleModelType } from '../../../data/vehicleModelType';
import useAssetModels from '../../../hooks/useAssetModels';
import type { OccurrenceWithTimeSpent } from '../../../models/occurrence';

export interface ClientOption {
    name: string;
    count: number;
}

export const useFilterOptions = () => {
    const { assetModels } = useAssetModels();
    const [cpvOptions, setCpvOptions] = useState<string[]>([]);

    useEffect(() => {
        fetchCpvs().then(setCpvOptions);
    }, []);

    const modelsByType = useMemo(() => {
        const grouped = new Map<VehicleModelType, Set<string>>();
        for (const model of assetModels) {
            const description = model.description?.trim();
            const type = getVehicleModelType(model.modelCode, description);
            if (description && type) {
                grouped.set(type, (grouped.get(type) ?? new Set()).add(description));
            }
        }
        return grouped;
    }, [assetModels]);

    const modelTypes = useMemo(
        () => VEHICLE_MODEL_TYPE_ORDER.filter(type => modelsByType.get(type)?.size),
        [modelsByType]
    );

    const modelOptions = useMemo(
        () =>
            [
                ...new Set(
                    assetModels.map(model => model.description?.trim()).filter((value): value is string => !!value)
                ),
            ].sort((a, b) => a.localeCompare(b)),
        [assetModels]
    );

    return { cpvOptions, modelsByType, modelTypes, modelOptions };
};

interface IClientOptionsInput {
    selectedClients: string[];
    finished: boolean;
    currentList: OccurrenceWithTimeSpent[];
    finalizedList: OccurrenceWithTimeSpent[];
}

export const useClientOptions = ({ selectedClients, finished, currentList, finalizedList }: IClientOptionsInput) => {
    const baseline = useRef<ClientOption[]>([]);

    return useMemo(() => {
        if (selectedClients.length) {
            return baseline.current;
        }
        const counts = new Map<string, number>();
        for (const item of finished ? finalizedList : currentList) {
            counts.set(item.customer, (counts.get(item.customer) ?? 0) + 1);
        }
        const options = [...counts.entries()]
            .map(([name, count]) => ({ name, count }))
            .sort((a, b) => a.name.localeCompare(b.name));
        baseline.current = options;
        return options;
    }, [selectedClients, finished, currentList, finalizedList]);
};
